import React, { useEffect, useRef, useState } from 'react'

import { find, size } from 'lodash'
import { useHistory } from 'react-router-dom'
import { FormContext, useFieldArray, useForm } from 'react-hook-form'
import { reverse } from 'named-urls'
import { Box, Button, Typography } from '@material-ui/core'

import { ContentHeader, LoadingFeedback } from 'components'

import { ChangesManagementQuestions, EvidencePlanAction } from './components'

import { routes } from 'Routes'
import * as service from 'service'
import constants from 'constants/index'
import helpers from 'helpers'
import theme from 'theme'

import useSnackbar from 'hooks/useSnackbar'

import schema from './schema'

const ChangesManagementForm = ({
  changesManagement,
  finalizeMode,
  refresh,
  isEdit = false,
  isShow = false,
}) => {
  const [isLoading, setIsLoading] = useState(false)

  const history = useHistory()
  const snackbar = useSnackbar()

  const getDefaultValues = (changesManagement = {}) => ({
    name: changesManagement?.name || '',
    plannedChange: changesManagement?.plannedChange || '',
    responsibleId:
      changesManagement?.responsibleId ||
      changesManagement?.responsible ||
      null,
    identifiedById:
      changesManagement?.identifiedById ||
      changesManagement?.identifiedBy ||
      null,
    openingDate: changesManagement?.openingDate || null,
    deadline: changesManagement?.deadline || null,
    conclusionDate: changesManagement?.conclusionDate || null,
    approvedById:
      changesManagement?.approvedById || changesManagement?.approvedBy || null,
    purposeOfChange: changesManagement?.purposeOfChange || '',
    evidencePlanActions: (changesManagement?.evidencePlanActions || []).map(
      (epa) => ({
        ...epa,
        originId: epa.id,
        evidence: {
          ...epa.evidence,
          attachments: [],
        },
        planActions: (epa.planActions || []).map((pa) => ({
          ...pa,
          originId: pa.id,
          responsible: pa.responsible || { id: null, name: '' },
        })),
      }),
    ),
  })

  const openingDateRef = useRef(null)
  const shouldFinalizeRef = useRef(false)

  const formMethods = useForm({
    validationSchema: schema(openingDateRef),
    defaultValues: getDefaultValues(changesManagement),
  })
  const { control, handleSubmit, reset, getValues, watch } = formMethods

  const watchedOpeningDate = watch('openingDate')
  useEffect(() => {
    openingDateRef.current = watchedOpeningDate
  }, [watchedOpeningDate])

  useEffect(() => {
    if (changesManagement) reset(getDefaultValues(changesManagement))
  }, [changesManagement])

  const lastEvidence = useRef()

  const { fields: evidencePlanActions, append: appendEvidencePlanAction } =
    useFieldArray({
      control,
      name: 'evidencePlanActions',
    })

  const responseUsers = async (inputValue) => {
    try {
      const response = await service.dponet.users.get({
        name: inputValue,
        perPage: 10000000,
        minimal: true,
        order: 'name ASC',
      })
      return response?.data?.users || []
    } catch {
      return []
    }
  }

  const handleAddEvidencePlanAction = () => {
    appendEvidencePlanAction({
      createdManually: true,
      evidence: {
        id: null,
        title: '',
        description: '',
        justify: '',
        attachments: [],
      },
      planActions: [],
    })

    setTimeout(() => {
      if (lastEvidence?.current)
        lastEvidence.current.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        })
    }, 100)
  }

  const handleRemoveEvidencePlanAction = (id) => {
    if (isShow) return
    const newEvidencePlanActions = evidencePlanActions.filter(
      (item) => item?.id !== id,
    )
    reset({ ...getValues(), evidencePlanActions: newEvidencePlanActions })
  }

  const onSubmit = async (data) => {
    const isFinalize = shouldFinalizeRef.current
    shouldFinalizeRef.current = false

    setIsLoading(true)

    const evidencePlanActionsData = data?.evidencePlanActions?.map(
      (action) => ({
        id: action?.originId,
        createdManually: action?.createdManually ?? true,
        evidence: {
          ...(!action?.evidence?.id && {
            title: action?.evidence?.title,
            description: action?.evidence?.description,
          }),
          justify: action?.evidence?.justify,
          attachments: action?.evidence?.attachments,
        },
        planActions: action?.planActions?.map((plan) => ({
          id: plan?.originId,
          responsibleUserId: plan?.responsible?.id,
          deadlineDate: helpers.formatters.toISOStringVerify(
            plan?.deadlineDate,
          ),
          what: plan?.what,
          how: plan?.how,
          because: plan?.because,
          where: plan?.where,
          howPrice: plan?.howPrice,
          status: plan?.status || constants.planAction.INITIAL_STATUS_ID,
        })),
      }),
    )

    const payload = {
      ...data,
      responsibleId: data?.responsibleId?.id,
      identifiedById: data?.identifiedById?.id,
      approvedById: data?.approvedById?.id,
      openingDate: helpers.formatters.toISOStringVerify(data?.openingDate),
      deadline: helpers.formatters.toISOStringVerify(data?.deadline),
      conclusionDate:
        isFinalize && !data?.conclusionDate
          ? new Date().toISOString()
          : helpers.formatters.toISOStringVerify(data?.conclusionDate),
      ...(isFinalize && { status: 'finalized' }),
      evidencePlanActions: evidencePlanActionsData,
    }

    try {
      if (isEdit) {
        await service.dponet.changesManagement.update({
          ...payload,
          changesManagementId: changesManagement?.id,
        })

        snackbar.open({
          message: isFinalize
            ? 'Gestão de mudança finalizada com sucesso!'
            : 'Gestão de mudança atualizada com sucesso!',
          variant: 'success',
        })

        if (isFinalize) history.push(routes.changesManagement.all)
        refresh()
      } else {
        const response = await service.dponet.changesManagement.create(payload)

        const newChangesManagement = response?.data?.changesManagement

        snackbar.open({
          message: 'Gestão de mudança criada com sucesso!',
          variant: 'success',
        })

        history.push(
          reverse(routes.changesManagement.edit, {
            changesManagementId: newChangesManagement?.id,
          }),
        )
      }
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <ContentHeader title="Gestão de Mudanças" />
      <LoadingFeedback open={isLoading} />
      <FormContext {...formMethods}>
        <form
          id="changes-management-form"
          onSubmit={(e) => {
            e.preventDefault()
            shouldFinalizeRef.current = finalizeMode?.current ?? false
            if (finalizeMode) finalizeMode.current = false
            handleSubmit(onSubmit)(e)
          }}
        >
          <ChangesManagementQuestions
            isShow={isShow}
            responseUsers={responseUsers}
          />
          <Box
            display="flex"
            flexDirection="column"
            gridGap={theme.spacing(2)}
            mt={2}
          >
            <Box
              width="100%"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="h4">Evidências</Typography>
              {!isShow && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddEvidencePlanAction}
                >
                  Nova evidência
                </Button>
              )}
            </Box>
            {evidencePlanActions?.map((evidencePlanAction, index) => {
              const defaultEvidence = find(
                changesManagement?.evidencePlanActions,
                {
                  id:
                    Number(evidencePlanAction?.id) ||
                    evidencePlanAction?.originId,
                },
              )

              return (
                <EvidencePlanAction
                  key={evidencePlanAction?.id}
                  ref={
                    index === size(evidencePlanActions) - 1
                      ? lastEvidence
                      : null
                  }
                  canEditActivity={!isShow}
                  changesManagementId={changesManagement?.id}
                  componentIndex={index}
                  defaultEvidencePlanAction={defaultEvidence}
                  disabledAll={isShow}
                  evidencePlanAction={evidencePlanAction}
                  isCreatedManually={
                    !defaultEvidence || defaultEvidence?.createdManually
                  }
                  planActionsOpened={[]}
                  removeEvidencePlanAction={() =>
                    handleRemoveEvidencePlanAction(evidencePlanAction?.id)
                  }
                  setLoading={setIsLoading}
                />
              )
            })}
          </Box>
        </form>
      </FormContext>
    </>
  )
}

export default ChangesManagementForm
